import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Calendar, Save } from 'lucide-react';
import { useWeekendSettings } from '@/hooks/useWeekendSettings';
import { SaturdayRescheduleType } from '@/types/billing-calendar';

interface WeekendSettingsCardProps {
  clientId: number;
}

const WeekendSettingsCard: React.FC<WeekendSettingsCardProps> = ({ clientId }) => {
  const { settings, loading, saving, saveSettings } = useWeekendSettings({ clientId });
  
  const [blockSunday, setBlockSunday] = useState(false);
  const [blockSaturday, setBlockSaturday] = useState(false);
  const [saturdayRescheduleType, setSaturdayRescheduleType] = useState<SaturdayRescheduleType>(null);

  // Sincronizar estado local com settings carregados
  useEffect(() => {
    if (settings) {
      setBlockSunday(settings.block_sunday);
      setBlockSaturday(settings.block_saturday);
      setSaturdayRescheduleType(settings.saturday_reschedule_type);
    }
  }, [settings]);

  const handleSave = async () => {
    const success = await saveSettings({
      block_sunday: blockSunday,
      block_saturday: blockSaturday,
      saturday_reschedule_type: blockSaturday ? saturdayRescheduleType : null
    });

    if (success) {
      // Settings já são atualizados pelo hook
    }
  };

  const hasChanges = settings && (
    blockSunday !== settings.block_sunday ||
    blockSaturday !== settings.block_saturday ||
    saturdayRescheduleType !== settings.saturday_reschedule_type
  );

  if (loading && !settings) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-gray-500">Carregando configurações...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-blue-200 bg-blue-50/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-800">
          <Calendar className="h-5 w-5" />
          Configurações de Dias Não Úteis
        </CardTitle>
        <CardDescription className="text-blue-600">
          Configure sábado e domingo como dias não úteis para reagendamento automático
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Toggle para Domingo */}
        <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-blue-200">
          <div className="flex-1">
            <Label htmlFor="block-sunday" className="text-base font-semibold text-gray-900 cursor-pointer">
              Bloquear Domingo
            </Label>
            <p className="text-sm text-gray-600 mt-1">
              Quando ativado, eventos agendados para domingo serão automaticamente reagendados para o próximo dia útil (segunda-feira)
            </p>
          </div>
          <Switch
            id="block-sunday"
            checked={blockSunday}
            onCheckedChange={setBlockSunday}
            disabled={loading || saving}
          />
        </div>

        {/* Toggle para Sábado */}
        <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-blue-200">
          <div className="flex-1">
            <Label htmlFor="block-saturday" className="text-base font-semibold text-gray-900 cursor-pointer">
              Bloquear Sábado
            </Label>
            <p className="text-sm text-gray-600 mt-1">
              Quando ativado, eventos agendados para sábado serão reagendados conforme a ação selecionada abaixo
            </p>
          </div>
          <Switch
            id="block-saturday"
            checked={blockSaturday}
            onCheckedChange={setBlockSaturday}
            disabled={loading || saving}
          />
        </div>

        {/* Select para ação do sábado (aparece apenas quando sábado está bloqueado) */}
        {blockSaturday && (
          <div className="p-4 bg-white rounded-lg border border-blue-200">
            <Label htmlFor="saturday-action" className="text-base font-semibold text-gray-900 mb-2 block">
              Ação para Sábado
            </Label>
            <Select
              value={saturdayRescheduleType || ''}
              onValueChange={(value) => setSaturdayRescheduleType(value as SaturdayRescheduleType)}
              disabled={loading || saving}
            >
              <SelectTrigger id="saturday-action">
                <SelectValue placeholder="Selecione uma ação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ANTECIPAR">Antecipar para Sexta-feira</SelectItem>
                <SelectItem value="PRORROGAR">Prorrogar para Segunda-feira</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-600 mt-2">
              {saturdayRescheduleType === 'ANTECIPAR' 
                ? 'Eventos serão movidos para a sexta-feira anterior'
                : saturdayRescheduleType === 'PRORROGAR'
                ? 'Eventos serão movidos para a segunda-feira seguinte'
                : 'Selecione uma ação para sábados bloqueados'}
            </p>
          </div>
        )}

        {/* Botão Salvar */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={loading || saving || !hasChanges}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Salvando...' : 'Salvar Configurações'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default WeekendSettingsCard;

